import React from "react";

import {
  isValidIconName,
  useIsMobile,
  WSAvatar,
  WSCard,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSIconName,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { ISubscription } from "@wingspanhq/users";
import { useHistory } from "react-router-dom";
import { useQueryPlanEnrollments } from "../../../query/benefits/useQueryPlanEnrollments";
import { IPlanType } from "../../../services/api/planEnrollment/types";
import { useGetNow } from "../../../shared/hooks/useGetNow";
import { getPathnameMyResourcesTab } from "../../MyResources/paths";
import { selectorAvailablePlanEnrollments } from "../selectors/selectorAvailablePlanEnrollments";
import { ExtendedWebflowBenefit } from "../types";
import { getCostOfBenefitWithSubscription } from "../utils";

interface BenefitsInsuranceBlockProps {
  benefit: ExtendedWebflowBenefit;
  subscription?: ISubscription;
  pagePath: string;
}

export const BenefitsInsuranceBlock: React.FC<BenefitsInsuranceBlockProps> = ({
  benefit,
  subscription,
  pagePath
}) => {
  const isMobile = useIsMobile();
  const now = useGetNow();

  const history = useHistory();

  const qPlanEnrollments = useQueryPlanEnrollments({
    filter: {
      planType: benefit.slug as IPlanType
    },
    sort: "createdAt"
  });

  const planEnrollment = qPlanEnrollments.data
    ? selectorAvailablePlanEnrollments(qPlanEnrollments.data, now).find(Boolean)
    : undefined;

  const isEnrolled = !!planEnrollment;

  const {
    atAGlance,
    logo,
    icon: iconString,
    helperCostDescription,
    iconBackgroundColor = "blue50",
    iconColor = "blue500",
    name,
    slug
  } = benefit;

  let icon: WSIconName =
    iconString && isValidIconName(iconString)
      ? iconString
      : "shield-check-fill";

  return (
    <WSCard
      key={slug}
      data-testid={`block-${slug}`}
      shimmer={qPlanEnrollments.isLoading}
      onClick={
        qPlanEnrollments.isFetched
          ? () => {
              if (isEnrolled) {
                history.push(
                  getPathnameMyResourcesTab("independent-contractor-insurance")
                );
              } else {
                history.push(pagePath);
              }
            }
          : undefined
      }
      mt="L"
      p="L"
    >
      <WSFlexBox.CenterY
        direction={isMobile ? "column" : "row"}
        alignContent={isMobile ? "flex-start" : "center"}
        alignItems={isMobile ? "flex-start" : "center"}
        justify="space-between"
        wrap="nowrap"
        gap="M"
      >
        <WSFlexBox.CenterY wrap="nowrap" pr="2XL" style={{ flexBasis: "65%" }}>
          <WSAvatar.Icon
            icon={icon}
            size="L"
            color={iconColor}
            colorBackground={iconBackgroundColor}
            mr="XL"
          />
          <WSElement>
            <WSText.Heading5>{name}</WSText.Heading5>
            <WSText.ParagraphXs color="gray500" mt="XS">
              {atAGlance}
            </WSText.ParagraphXs>
          </WSElement>
        </WSFlexBox.CenterY>

        {!benefit.hideCost && (
          <WSElement style={{ flexBasis: "25%" }}>
            {isEnrolled ? (
              <WSPill theme="blue" badge text="Enrolled" />
            ) : (
              <WSText.ParagraphSm>
                Cost:{" "}
                {benefit.shortCostDescription ||
                  getCostOfBenefitWithSubscription(benefit, subscription)}
              </WSText.ParagraphSm>
            )}
          </WSElement>
        )}

        {!isMobile && (
          <WSFlexBox style={{ flexBasis: "10%" }} justify="flex-end">
            <WSIcon block name="chevron-right" color="blue500" />
          </WSFlexBox>
        )}
      </WSFlexBox.CenterY>
    </WSCard>
  );
};
